import { useEffect, useState } from "react";
import {
  Box, Button, Card, Collapse, IconButton, Stack, Tab, Tabs,
  Tooltip, Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import type {
  DocRowStatus, DocumentRow, InvestigatorAssignment,
} from "@/api/types";
import { brand } from "@/theme/brandColors";
import { InvestigatorDocumentsTable } from "./InvestigatorDocumentsTable";
import { RollupStatusChip, rollupDocStatuses } from "./RollupStatusChip";

interface Props {
  pi: InvestigatorAssignment | null;
  subInvestigators: InvestigatorAssignment[];
  piDocuments: DocumentRow[];
  subInvestigatorDocuments: Record<string, DocumentRow[]>;
  onAddSub: () => void;
  onReplacePI: () => void;
  onRemoveSub: (assignmentId: string) => void;
}

/** One dot per required document. Green when the doc is current,
 * red when missing or expired, yellow when expiring, gray when
 * still being collected. */
const DOC_DOT_COLOR: Record<DocRowStatus, string> = {
  current: "#16a34a",
  expiring: "#eab308",
  expired: "#dc2626",
  missing: "#dc2626",
  in_progress: "#d1d5db",
};

const STATUS_TEXT: Record<DocRowStatus, string> = {
  current: "current",
  expiring: "expiring soon",
  expired: "expired",
  missing: "missing",
  in_progress: "in progress",
};

interface InvestigatorTab {
  id: string;            // PI uses "pi", Sub-Is use their assignment_id
  kind: "pi" | "sub_investigator";
  assignment: InvestigatorAssignment;
  documents: DocumentRow[];
}

/**
 * Tabbed PI / Sub-I panel — one tab per investigator with the
 * person's name + a row of doc-status dots in the tab label.
 * Selected tab shows the profile + required-document table
 * underneath. Mirrors how locations used to render before we
 * switched them to the inline-collapse pattern; the tab layout
 * keeps multiple investigators visually together.
 */
export function CohortInvestigators({
  pi, subInvestigators, piDocuments, subInvestigatorDocuments,
  onAddSub, onReplacePI, onRemoveSub,
}: Props) {
  const tabs: InvestigatorTab[] = [];
  if (pi) {
    tabs.push({ id: "pi", kind: "pi", assignment: pi, documents: piDocuments });
  }
  for (const s of subInvestigators) {
    tabs.push({
      id: s.assignment_id, kind: "sub_investigator", assignment: s,
      documents: subInvestigatorDocuments[s.investigator.id] ?? [],
    });
  }

  const [activeId, setActiveId] = useState<string | null>(tabs[0]?.id ?? null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (tabs.length === 0) {
      if (activeId !== null) setActiveId(null);
      return;
    }
    if (!tabs.some((t) => t.id === activeId)) {
      setActiveId(tabs[0].id);
    }
  }, [tabs, activeId]);

  const active = tabs.find((t) => t.id === activeId) ?? null;

  if (tabs.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: 2, display: "flex", alignItems: "center", gap: 1,
          fontStyle: "italic", color: "text.secondary", fontSize: 13,
        }}
      >
        No PI assigned.
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onReplacePI}>Assign PI</Button>
        <Button size="small" onClick={onAddSub}>+ Add Sub-I</Button>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 0 }}>
      {/* Collapsible header — clicking anywhere on the strip toggles
          the body. Keeps the Add Sub-I action live by stopping the
          toggle on its own click. */}
      <Box
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        aria-controls="cohort-investigators-body"
        sx={{
          p: 1.5, borderBottom: open ? 1 : 0, borderColor: "divider",
          bgcolor: brand.ocean,
          color: brand.pearl,
          cursor: "pointer", userSelect: "none",
          display: "flex", alignItems: "center", gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: brand.pearl }}>
          Investigators · {tabs.length}
        </Typography>
        <RollupStatusChip
          level={rollupDocStatuses(
            tabs.flatMap((t) => t.documents.map((d) => d.status)),
          )}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); onAddSub(); }}
          sx={{ color: brand.pearl, "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
        >
          + Add Sub-I
        </Button>
        <IconButton
          size="small"
          aria-label={open ? "Collapse investigators" : "Expand investigators"}
          sx={{ color: brand.pearl }}
        >
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit>
      <Box id="cohort-investigators-body">
      <Box sx={{
        display: "flex", alignItems: "center",
        borderBottom: 1, borderColor: "divider",
      }}>
        <Tabs
          value={active?.id ?? false}
          onChange={(_, v) => setActiveId(v as string)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1 }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={{ fontWeight: 500 }}>
                    {t.assignment.investigator.first_name}{" "}
                    {t.assignment.investigator.last_name}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    {t.documents.map((d) => (
                      <Tooltip
                        key={d.doc_type}
                        title={`${d.label}: ${STATUS_TEXT[d.status]}`}
                      >
                        <Box
                          sx={{
                            width: 9, height: 9, borderRadius: "50%",
                            bgcolor: DOC_DOT_COLOR[d.status],
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              }
              sx={{ textTransform: "none", minHeight: 44 }}
            />
          ))}
        </Tabs>
      </Box>

      {active && (
        <Box sx={{ p: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Profile · {active.kind === "pi" ? "PI" : "Sub-I"}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body2">
                {active.assignment.investigator.email ?? "—"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Phone</Typography>
              <Typography variant="body2">
                {active.assignment.investigator.phone ?? "—"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">NPI</Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}
              >
                {active.assignment.investigator.npi ?? "—"}
              </Typography>
            </Box>
          </Stack>

          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Required documents
          </Typography>
          <InvestigatorDocumentsTable
            rows={active.documents}
            investigatorId={active.assignment.investigator.id}
          />

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {active.kind === "pi" ? (
              <Button size="small" color="error" onClick={onReplacePI}>Replace PI</Button>
            ) : (
              <Button
                size="small"
                color="error"
                onClick={() => onRemoveSub(active.assignment.assignment_id)}
              >
                Remove
              </Button>
            )}
          </Box>
        </Box>
      )}
      </Box>
      </Collapse>
    </Card>
  );
}
